"use client";

import { useTranslations } from "next-intl";
import { useIsMobile } from "@/features/layout/useIsMobile";
import { cn } from "@/lib/cn";
import { useItemsInfos } from "../hooks/useItems";
import { useSelectedItemId } from "../hooks/useSelectedItemId";
import { useListViewType, type ListViewType } from "../useListViewType";
import { ListOrderBy } from "./ListOrderBy";

/**
 * The count and the list/cards switch.
 *
 * The switch is desktop-only, because the mobile panel always shows a list.
 */
export function ResultHeader() {
  const t = useTranslations();
  const isMobile = useIsMobile();
  const { count } = useItemsInfos();
  const { viewType, setViewType } = useListViewType();
  const { selectedItemId } = useSelectedItemId();

  return (
    <div className="grid grid-cols-2 grid-rows-1 items-center bg-white pb-2 pl-5 pr-5 dark:bg-dark-color">
      <div className="flex flex-row text-sm">
        <span className="mr-5">
          <span className="mr-1 font-bold">{count}</span> {t("results.title")}
        </span>

        {!isMobile && (
          <>
            <ViewTypeButton
              viewType="list"
              current={viewType}
              onSelect={setViewType}
              label={t("items.listView")}
              icon="ic-mpr_list"
              // The details panel covers the list while a POI is open, so its
              // controls must not be reachable by tab behind it.
              isReachable={!selectedItemId}
              className="mr-5"
            />
            <ViewTypeButton
              viewType="cards"
              current={viewType}
              onSelect={setViewType}
              label={t("items.cardView")}
              icon="ic-mpr_cards"
              isReachable={!selectedItemId}
            />
          </>
        )}
      </div>

      <ListOrderBy />
    </div>
  );
}

function ViewTypeButton({
  viewType,
  current,
  onSelect,
  label,
  icon,
  isReachable,
  className,
}: {
  viewType: ListViewType;
  current: ListViewType;
  onSelect: (viewType: ListViewType) => void;
  label: string;
  icon: string;
  isReachable: boolean;
  className?: string;
}) {
  const isActive = current === viewType;

  return (
    <button
      type="button"
      tabIndex={isReachable ? 0 : -1}
      title={label}
      aria-label={label}
      aria-pressed={isActive}
      onClick={() => onSelect(viewType)}
      className={cn(
        "focus-visible:outline focus-visible:outline-blue-500",
        // Inactive inherits the ambient colour. The legacy class here was
        // `text-muted`, which is defined neither by the Tailwind config nor by
        // Flowbite — a Bootstrap leftover that styled nothing, so this renders
        // the same and doesn't claim otherwise.
        isActive && "text-primary",
        className
      )}
    >
      <i className={cn("icon cursor-pointer", icon)} aria-hidden="true" />
    </button>
  );
}
